<div class="attendance-page">

  <div class="page-header">
    <div>
      <div class="breadcrumb">HR Management / Attendance</div>
      <h1>Attendance</h1>
      <p>Employee attendance import, dashboard metrics, calendar, and reconciliation.</p>
    </div>
    <button mat-raised-button color="primary" type="button" (click)="refresh()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
  </div>

  @if (error) {
    <p class="banner error">{{ error }}</p>
  }
  @if (importMessage) {
    <p class="banner ok">{{ importMessage }}</p>
  }

  <section class="toolbar cardish">
    <mat-form-field appearance="outline">
      <mat-label>Employee</mat-label>
      <mat-select [(ngModel)]="selectedEmployeeId" (selectionChange)="onEmployeeChange()">
        @for (e of employees; track e.id) {
          <mat-option [value]="e.id">{{ e.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Period</mat-label>
      <mat-select [(ngModel)]="periodType" (selectionChange)="onPeriodChange()">
        @for (p of periodOptions; track p) {
          <mat-option [value]="p">{{ p }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="month-field">
      <mat-label>Month</mat-label>
      <input matInput type="number" min="1" max="12" [(ngModel)]="selectedMonth" (change)="onPeriodChange()" />
    </mat-form-field>

    <mat-form-field appearance="outline" class="month-field">
      <mat-label>Year</mat-label>
      <input matInput type="number" [(ngModel)]="selectedYear" (change)="onPeriodChange()" />
    </mat-form-field>

    <button mat-stroked-button type="button" (click)="exportAttendanceCsv()"
      matTooltip="Export selected employee's CSV for the selected month"
      [disabled]="!selectedEmployeeId">
      <mat-icon>download</mat-icon>
      Export month CSV
    </button>
    <button mat-stroked-button type="button" (click)="downloadAttendanceTemplate()">
      <mat-icon>description</mat-icon>
      CSV Template
    </button>
  </section>

  @if (summary) {
    <section class="kpi-grid">
      <div class="kpi"><span class="label">Working Days</span><strong>{{ summary.totalWorkingDays }}</strong></div>
      <div class="kpi"><span class="label">Present</span><strong>{{ summary.presentDays }}</strong></div>
      <div class="kpi"><span class="label">Absent</span><strong>{{ summary.absentDays }}</strong></div>
      <div class="kpi"><span class="label">Leave</span><strong>{{ summary.leaveDays }}</strong></div>
      <div class="kpi"><span class="label">Late</span><strong>{{ summary.lateCount }}</strong></div>
      <div class="kpi"><span class="label">Early Exit</span><strong>{{ summary.earlyExitCount }}</strong></div>
      <div class="kpi"><span class="label">Working Hours</span><strong>{{ summary.workingHours }}</strong></div>
      <div class="kpi"><span class="label">Overtime</span><strong>{{ summary.overtimeHours }}</strong></div>
    </section>
  }

  <mat-tab-group class="attendance-tabs">
    <mat-tab label="Records">
      <mat-card class="attendance-card">
        <div class="filters">
          @if (canEditReconciliation) {
            <button mat-raised-button color="primary" type="button" (click)="loadReconciliation()">
              Load Reconciliation
            </button>
            <button mat-raised-button color="accent" type="button" (click)="saveReconciliation()">
              Save
            </button>
          }

          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Search attendance</mat-label>
            <input matInput [(ngModel)]="searchText" (ngModelChange)="filterRecords()" placeholder="Employee name or date" />
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Status</mat-label>
            <mat-select [(ngModel)]="selectedStatus" (selectionChange)="filterRecords()">
              @for (s of statuses; track s) {
                <mat-option [value]="s">{{ s }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <button mat-button type="button" (click)="clearFilters()">Clear Filters</button>
        </div>

        <mat-divider></mat-divider>

        @if (loading) {
          <div class="loading-container">
            <mat-spinner diameter="45"></mat-spinner>
            <p>Loading attendance records...</p>
          </div>
        } @else {
          <div class="table-container">
            <table mat-table [dataSource]="filteredRecords" class="attendance-table">
              <ng-container matColumnDef="employee">
                <th mat-header-cell *matHeaderCellDef>Employee</th>
                <td mat-cell *matCellDef="let record">{{ record.employeeName }}</td>
              </ng-container>
              <ng-container matColumnDef="date">
                <th mat-header-cell *matHeaderCellDef>Date</th>
                <td mat-cell *matCellDef="let record">{{ record.date }}</td>
              </ng-container>
              <ng-container matColumnDef="checkIn">
                <th mat-header-cell *matHeaderCellDef>Check In</th>
                <td mat-cell *matCellDef="let record">{{ record.checkIn }}</td>
              </ng-container>
              <ng-container matColumnDef="checkOut">
                <th mat-header-cell *matHeaderCellDef>Check Out</th>
                <td mat-cell *matCellDef="let record">{{ record.checkOut }}</td>
              </ng-container>
              <ng-container matColumnDef="expectedHours">
                <th mat-header-cell *matHeaderCellDef>Hours</th>
                <td mat-cell *matCellDef="let record">{{ record.expectedHours }}</td>
              </ng-container>
              <ng-container matColumnDef="overtimeHours">
                <th mat-header-cell *matHeaderCellDef>OT</th>
                <td mat-cell *matCellDef="let record">{{ record.overtimeHours }}</td>
              </ng-container>
              <ng-container matColumnDef="lateMinutes">
                <th mat-header-cell *matHeaderCellDef>Late (min)</th>
                <td mat-cell *matCellDef="let record">{{ record.lateMinutes }}</td>
              </ng-container>
              <ng-container matColumnDef="earlyExit">
                <th mat-header-cell *matHeaderCellDef>Early (min)</th>
                <td mat-cell *matCellDef="let record">{{ record.earlyExitMinutes }}</td>
              </ng-container>
              <ng-container matColumnDef="actualHours">
                <th mat-header-cell *matHeaderCellDef>Approved Hours</th>
                <td mat-cell *matCellDef="let record">
                  @if (canEditReconciliation) {
                    <input matInput type="number" min="0" step="0.25" [(ngModel)]="record.actualHours" />
                  } @else {
                    {{ record.actualHours ?? '—' }}
                  }
                </td>
              </ng-container>
              <ng-container matColumnDef="isRelaxed">
                <th mat-header-cell *matHeaderCellDef>Relaxation</th>
                <td mat-cell *matCellDef="let record">
                  @if (canEditReconciliation) {
                    <input type="checkbox" [(ngModel)]="record.isRelaxed" />
                  } @else {
                    {{ record.isRelaxed ? 'Yes' : 'No' }}
                  }
                </td>
              </ng-container>
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let record">
                  <span class="status-badge"
                    [class.present]="record.status === 'Present'"
                    [class.absent]="record.status === 'Absent'"
                    [class.late]="record.status === 'Late'"
                    [class.leave]="record.status === 'Leave'">
                    {{ record.status }}
                  </span>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
            </table>

            @if (filteredRecords.length === 0) {
              <div class="no-data">
                <mat-icon>event_busy</mat-icon>
                <h3>No Attendance Records Found</h3>
                <p>Try changing filters or import a CSV for the selected employee.</p>
              </div>
            }
          </div>
        }
      </mat-card>
    </mat-tab>

    <mat-tab label="Calendar">
      <div class="calendar-wrap">
        <div class="calendar-grid">
          @for (day of calendarDays; track day.date) {
            <div [class]="dayClass(day)" [matTooltip]="day.holidayName || day.status || ''">
              <div class="day-num">{{ day.date | slice:8:10 }}</div>
              <div class="day-meta">
                @if (day.isHoliday) { H }
                @else if (day.onLeave) { L }
                @else if (day.status != null) { {{ normalizeStatusLabel(day.status) }} }
                @else if (!day.isWeekend) { — }
              </div>
              @if (day.workedHours) {
                <div class="day-hours">{{ day.workedHours }}h</div>
              }
            </div>
          }
        </div>
        <div class="legend">
          <span class="present">Present</span>
          <span class="late">Late</span>
          <span class="absent">Absent</span>
          <span class="leave">Leave</span>
          <span class="holiday">Holiday</span>
          <span class="weekend">Weekend</span>
        </div>
      </div>
    </mat-tab>

    @if (canImport) {
      <mat-tab label="CSV Import / Export">
        <section class="import-panel">
          <h3>Monthly CSV — one employee at a time</h3>
          <p class="hint">
            Select an <strong>employee</strong> and <strong>month/year</strong> above, then export or import.
            Columns: <code>Date,CheckIn,CheckOut,Status,Notes,Location</code>.
            Only rows inside the selected month are imported; duplicate dates are skipped.
          </p>
          <div class="import-actions">
            <button mat-stroked-button type="button" (click)="exportAttendanceCsv()" [disabled]="!selectedEmployeeId">
              <mat-icon>download</mat-icon>
              Export this employee / month
            </button>
            <button mat-stroked-button type="button" (click)="downloadAttendanceTemplate()">
              <mat-icon>description</mat-icon>
              Template
            </button>
            <input #csvInput type="file" hidden accept=".csv,text/csv" (change)="onCsvPicked($event)" />
            <button mat-stroked-button type="button" (click)="csvInput.click()" [disabled]="!selectedEmployeeId">
              Choose CSV
            </button>
            <span class="file-name">{{ csvFile?.name || 'No file selected' }}</span>
            <button mat-raised-button color="primary" type="button"
              [disabled]="uploading || !csvFile || !selectedEmployeeId"
              (click)="uploadCsv()">
              {{ uploading ? 'Importing…' : 'Import this employee / month' }}
            </button>
          </div>

          @if (imports.length) {
            <h4>Recent imports for selected employee</h4>
            <div class="imports-list">
              @for (imp of imports; track imp.id) {
                <div class="import-row">
                  <span>{{ imp.originalFileName }}</span>
                  <span>{{ imp.uploadedAt | date:'short' }}</span>
                  <span>{{ imp.status }}</span>
                  <span>+{{ imp.importedRows }} / dup {{ imp.duplicateRows }} / bad {{ imp.invalidRows }}</span>
                </div>
              }
            </div>
          }
        </section>
      </mat-tab>
    }

    <mat-tab label="Reports">
      <section class="report-panel">
        <p>Period: <strong>{{ periodType }}</strong> · {{ selectedMonth }}/{{ selectedYear }}</p>
        @if (reportTotals) {
          <div class="kpi-grid compact">
            <div class="kpi"><span class="label">Working Days</span><strong>{{ reportTotals.totalWorkingDays }}</strong></div>
            <div class="kpi"><span class="label">Present</span><strong>{{ reportTotals.presentDays }}</strong></div>
            <div class="kpi"><span class="label">Absent</span><strong>{{ reportTotals.absentDays }}</strong></div>
            <div class="kpi"><span class="label">Leave</span><strong>{{ reportTotals.leaveDays }}</strong></div>
            <div class="kpi"><span class="label">Late</span><strong>{{ reportTotals.lateCount }}</strong></div>
            <div class="kpi"><span class="label">Early Exit</span><strong>{{ reportTotals.earlyExitCount }}</strong></div>
            <div class="kpi"><span class="label">Hours</span><strong>{{ reportTotals.workingHours }}</strong></div>
            <div class="kpi"><span class="label">OT</span><strong>{{ reportTotals.overtimeHours }}</strong></div>
          </div>
        } @else {
          <button mat-stroked-button type="button" (click)="loadReport()">Load report totals</button>
        }
        @if (summary) {
          <p class="hint">Employee view uses the same metrics as Payroll will consume later (approved hours: {{ summary.approvedWorkingHours }}).</p>
        }
      </section>
    </mat-tab>
  </mat-tab-group>

</div>
